import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, effect, HostListener, inject, Input, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NbAccordionModule, NbButtonModule, NbCardModule, NbCheckboxModule, NbDialogRef, NbDialogService, NbInputModule, NbListModule, NbOptionModule, NbPopoverDirective, NbPopoverModule, NbSpinnerModule } from '@nebular/theme';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, finalize, first, forkJoin, Observable, of, Subject, Subscription, switchMap } from 'rxjs';
import { KPI } from '../../../../@core/api';
import { AssetsService, ChartsService, I18nService, InitiativesChangesService, InvestmentNotationsService, InvestmentsService, PerimetersService, RoadmapService, SaveToastService, ScopeService, StateLevelsService, YearsService } from '../../../../@core/utils';
import { IdMap } from '../../../../@core/utils/scope.service';
import { InvestmentWizardService } from '../../../../@core/utils/investment-wizard-service';
import { CurrencyPipe } from '../../../pipes';
import { BasicDialogComponent } from '../../basic-dialog/basic-dialog.component';
import { InvestmentWizardRootComponent } from '../../wizards/investment-wizard/wizard-index.component';
import { ComplementaryInvestmentChoiceComponent } from '../complementary-investment-choice/complementary-investment-choice.component';
import { InvestmentFormComponent } from '../investment-form/investment-form.component';
import { InvestmentsSelectableListComponent } from '../investments-selectable-list/investments-selectable-list.component';
import { RightPanelDialogService } from '../../../../@core/utils/right-panel-dialog.service';

@Component({
  selector: 'app-linkable-investments-list',
  standalone: true,
  imports: [
    CommonModule,
    NbCardModule,
    TranslateModule,
    NbSpinnerModule,
    CurrencyPipe,
    NbCheckboxModule,
    NbAccordionModule,
    NbButtonModule,
    FormsModule,
    ReactiveFormsModule,
    NbInputModule,
    NbPopoverModule,
    NbListModule,
    NbOptionModule,
    ComplementaryInvestmentChoiceComponent,
  ],
  templateUrl: './linkable-investments-list.component.html',
  styleUrls: ['./linkable-investments-list.component.scss'],
})
export class LinkableInvestmentsListComponent extends InvestmentsSelectableListComponent implements OnInit {


  @Input() public mainInvestmentId: number;
  @Input() public linkedToId: number | null = null;
  @Input() public readOnly: boolean = false;
  @Input() public isMainInvestmentReadOnly: boolean = false;
  @Input() public initiative: Partial<KPI.MappedInitiative> = null;
  @Input() public multipleChoice: boolean = true; // If true, the user can select multiple investments
  @Input() public canLink: boolean = true;
  @Input() asset: KPI.Asset = null;
  @ViewChild(NbPopoverDirective) choicePopover: NbPopoverDirective;

  private destroyRef = inject(DestroyRef);
  private readonly sideFormLevelService = inject(RightPanelDialogService);
  private searchSubject = new Subject<string>();
  private resultsSubscription: Subscription | null = null;
  private ivWizardService = inject(InvestmentWizardService);
  private assetsService = inject(AssetsService);
  private investmentNotationsService = inject(InvestmentNotationsService);
  private stateLevelsService = inject(StateLevelsService);
  private scopeService = inject(ScopeService);
  private investmentTypesMap = toSignal(this.scopeService.investmentTypes$, {
    initialValue: {} as IdMap<KPI.InvestmentType>,
  });

  private saveToast = inject(SaveToastService);
  public currentMode = signal<'links' | 'search'>('links');
  public menuMode: 'investment' | 'initiative' | 'asset' = 'investment';
  public showAttachMenu: boolean = false;

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.showAttachMenu && !target.closest('.initiative-attach-menu-container')) {
      this.showAttachMenu = false;
    }
  }

  public get displayedTitle(): string {
    if (this.mainInvestmentId) {
      if (this.currentMode() === 'search') {
        return this.translate.instant('Investments');
      }
      return this.translate.instant('Linked investment(s)');
    } else if (this.initiative) {
      if (this.currentMode() === 'search') {
        return this.translate.instant('Link an existing investment to the action');
      }
      return this.translate.instant('Investment linked to the action');
    } else if (this.asset) {
      return this.translate.instant('Attach to the equipment an existing investment');
    }
  }

  constructor(
    private initiativesChanges: InitiativesChangesService,
    private chartsService: ChartsService,
    private yearsService: YearsService,
  ) {
    super(
        inject(NbDialogRef<LinkableInvestmentsListComponent>),
        inject(NbDialogService),
        inject(InvestmentsService),
        inject(TranslateService),
        inject(I18nService),
        inject(RoadmapService),
    );

    // Set up debounced search
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.searchValue.set(value);
    });

    // Handle mode changes - load results based on current mode
    effect(() => {
      if (this.currentMode() === 'search') {
        this.loadInitialSearch();
      } else if (this.currentMode() === 'links') {
        this.loadLinkedInvestments();
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (!!this.initiative) {
      this.menuMode = 'initiative';
    } else if (!!this.asset) {
      this.menuMode = 'asset';
      if (this.inputLinkedElementIds?.length) {
        this.linkedElementIds.set(this.inputLinkedElementIds);
      }
      this.currentMode.set('search');
    }
    this.yearsService.latestYears$.subscribe();
  }

  private loadLinkedInvestments(): void {
    this.allElementsLoading.set(true);
    this.resultsSubscription?.unsubscribe();

    if (this.linkedToId) {
      // Current is secondary: fetch main (for links_from) → build full group incl. current
      this.investmentsService.getInvestmentRaw(this.linkedToId).pipe(
        switchMap((mainRaw: any) => {
          const siblingIds: number[] = (mainRaw?.links_from || [])
            .map((l: any) => typeof l === 'object' ? l.id : l)
            .filter((id: number) => id !== this.mainInvestmentId);

          // Include main + siblings + current investment itself
          const memberIds = [this.linkedToId, ...siblingIds, this.mainInvestmentId];
          return forkJoin(
            memberIds.map(id => this.investmentsService.getInvestment(id) as Observable<KPI.Investment>)
          );
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.allElementsLoading.set(false)),
      ).subscribe((groupMembers: KPI.Investment[]) => {
        this.allElements.set(groupMembers.filter(inv => !!inv));
        this.linkedElementIds.set(groupMembers.filter(inv => !!inv).map(e => e.id));
      });
      return;
    }

    if (this.mainInvestmentId) {
      // Current is main: fetch itself + all children
      this.investmentsService.getInvestmentRaw(this.mainInvestmentId).pipe(
        switchMap((mainRaw: any) => {
          const childIds: number[] = (mainRaw?.links_from || [])
            .map((l: any) => typeof l === 'object' ? l.id : l);

          // Standalone investment — no links at all
          if (childIds.length === 0) return of([] as KPI.Investment[]);

          // Include current investment (main) + all children
          const allIds = [this.mainInvestmentId, ...childIds];
          return forkJoin(
            allIds.map(id => this.investmentsService.getInvestment(id) as Observable<KPI.Investment>)
          );
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.allElementsLoading.set(false)),
      ).subscribe((elements: KPI.Investment[]) => {
        this.allElements.set(elements.filter(inv => !!inv));
        this.linkedElementIds.set(elements.filter(inv => !!inv).map(e => e.id));
      });
      return;
    }

    if (this.initiative?.investment) {
      this.allElements.set([this.initiative.investment] as KPI.Investment[]);
      this.linkedElementIds.set([this.initiative.investment.id]);
    }
    this.allElementsLoading.set(false);
  }

  public getBudgetDisplay(elt: KPI.Investment): string {
    const price = (elt as any).slices?.[0]?.price || (elt as any).budget_reference || (elt as any).current_price || 0;
    const priceFmt = price ? this.i18nService.formatPriceWithThousands(price) : '-';
    const year = this.translate.instant('in {{year}}', { year: elt.final_schedule_to });
    const origin = (elt as any).budget_origin?.name || this.translate.instant('Other');
    return `${priceFmt} ${year} (${origin})`;
  }

  // Override to perform custom data loading
  // Note: loadLinkedInvestments is NOT called here — the effect() in constructor handles it
  protected override setUpData(): void {
    this.linkedElementIds.set(this.inputLinkedElementIds);
  }

  protected override notLinkedElementsAfterFilter = computed(() => {
    const search = this.searchValue()?.toLowerCase() || '';
    return this.allElements().filter(element =>
        !this.linkedElementIds().includes(element['id'])
        && this.showInNotLinkedElements(element)
        && (!search || element['label']?.toLowerCase().includes(search))
    );
  });

  public loadInitialSearch(): void {
    this.allElements.set([]);
    this.allElementsLoading.set(true);
    this.resultsSubscription?.unsubscribe();

    this.resultsSubscription = this.getAllElements$().pipe(
      finalize(() => this.allElementsLoading.set(false))
    ).subscribe((elements) => {
      this.allElements.set(elements);
    });
  }

  public override search(value: string): void {
    this.searchSubject.next(value);
  }

  protected override showInNotLinkedElements(investment: KPI.Investment): boolean {
    if (investment.id === this.mainInvestmentId) {
      return false;
    }

    if (this.asset) {
      if (investment.asset) {
        return false;
      }

      const investmentType = (investment as any).investment_type_object
        ?? this.investmentTypesMap()[investment.investment_type];
      if (investmentType?.creation_of_equipment || investmentType?.replacement) {
        return false;
      }
    }

    return true;
  }

  public override notLinkedElementChecked(element: KPI.Investment): void {
    const status = (element as any).status_object;

    // Check validation constraints
    const isClosed = status?.is_closed === true;
    const isLinked = !this.asset && !!element.linked_to;

    let errorMessage = '';
    if (isClosed) {
      errorMessage = 'A complementary investment must not be already closed';
    } else if (isLinked) {
      errorMessage = 'The investment is already linked to another investment';
    }

    if (errorMessage) {
      this.selectedIds.update((ids) => ids.filter((id) => id !== element.id));
      this.dialogService.open(BasicDialogComponent, {
        autoFocus: false,
        context: {
          title: this.translate.instant('Impossible to attach the investment'),
          content: this.translate.instant(errorMessage),
          canCancel: false,
        },
      });
      return;
    }

    super.notLinkedElementChecked(element);
  }

  protected override getAllElements$(): Observable<KPI.Investment[]> {
    if (!this.perimeter) {
      return of([]);
    }

    const buildingIds = PerimetersService.getBuildingsIds([this.perimeter]);
    const hasAction = this.initiative ? false : null;
    const extraParams: any = { has_action: hasAction };

    return this.yearsService.yearsRange$.pipe(
      switchMap(yearsRange => this.investmentsService.loadInvestments(
        yearsRange,
        buildingIds,
        null,
        null,
        true,
        extraParams
      )),
      first(),
    ) as Observable<KPI.Investment[]>;
  }

  public override linkElements(): void {
    const selectedIds = this.selectedIds();
    if (selectedIds.length === 0) return;

    this.allElementsLoading.set(true);

    this.linkSelectedInvestments(selectedIds).pipe(
      finalize(() => this.allElementsLoading.set(false)),
    ).subscribe({
      next: () => {
        this.chartsService.capexHasChanged();
        this.investmentsService.investmentLinksChanged$.next();
        if (this.mainInvestmentId) {
          this.investmentsService.reloadInvestment$.next(this.mainInvestmentId);
          // After linking, current investment is now a child of selectedIds[0].
          // Update linkedToId so loadLinkedInvestments() fetches the full group correctly.
          this.linkedToId = selectedIds[0];
          this.close();
        } else {
          this.close(selectedIds);
        }
      },
    });
  }

  private linkSelectedInvestments(selectedIds: number[]): Observable<any> {
    if (this.mainInvestmentId) {
      // Link current investment as child of the selected parent
      return this.investmentsService.linkInvestment(this.mainInvestmentId, selectedIds[0]).pipe(first());
    } else {
      return of(selectedIds);
    }
  }

  public setSearchMode(): void {
    this.showAttachMenu = false;
    this.currentMode.set('search');
  }

  public handleAddChoice(choice: 'existing' | 'new'): void {
    this.choicePopover?.hide();
    if (choice === 'existing') {
      this.currentMode.set('search');
    } else if (choice === 'new') {
      this.close('new');
    }
  }

  public toggleAttachMenu(): void {
    this.showAttachMenu = !this.showAttachMenu;
  }

  public closeAttachMenu(): void {
    this.showAttachMenu = false;
  }

  public openNewInvestment(): void {
    this.showAttachMenu = false;
    this.close('new');
  }

  public override removeLinkedElement(elementId: number): void {
    this.allElementsLoading.set(true);

    if (this.mainInvestmentId) {
      const isUnlinkingCurrentFromParent = !!this.linkedToId && elementId === this.linkedToId;
      const investmentIdToUnlink = isUnlinkingCurrentFromParent ? this.mainInvestmentId : elementId;

      this.investmentsService.unlinkInvestment(investmentIdToUnlink).pipe(
        first(),
        finalize(() => this.allElementsLoading.set(false))
      ).subscribe(() => {
        // Reload AG Grid and notify parent components
        this.chartsService.capexHasChanged();
        this.investmentsService.investmentLinksChanged$.next();
        this.investmentsService.reloadInvestment$.next(this.mainInvestmentId);

        if (isUnlinkingCurrentFromParent) {
          this.linkedToId = null;
          this.allElements.set([]);
          this.linkedElementIds.set([]);
          this.close([]);
          return;
        }

        // Optimistic update
        this.linkedElementIds.update(ids => ids.filter(id => id !== elementId));
        this.allElements.update(elts => elts.filter(e => e.id !== elementId));

        // If no linked investments remain (only the main itself), clear the list
        const remaining = this.allElements().filter(e => e.id !== this.mainInvestmentId);
        if (remaining.length === 0) {
          this.allElements.set([]);
          this.linkedElementIds.set([]);
        }
      });
    } else if (this.initiative) {
      // unlink from initiative
      this.unlinkInvestmentAndInitiative();
      // Optimistic update
      this.linkedElementIds.update(ids => ids.filter(id => id !== elementId));
      this.allElements.update(elts => elts.filter(e => e.id !== elementId));
      this.allElementsLoading.set(false)
    }
  }

  public override close(result?: any): void {
    if (this.currentMode() === 'search' && !result && !this.asset) {
        this.currentMode.set('links');
    } else {
        this.customNbCardClass = 'slide-out';
        setTimeout(() => this.nbDialogRef.close(result || this.linkedElementIds() || []), 150);
    }
  }

  private unlinkInvestmentAndInitiative() {
    const change = [{ property: "investment", value: null }];
    this.initiativesChanges.addChanges(this.initiative.id, change);
    this.initiative.budget = 0;
    this.initiative.investment = null;
    this.chartsService.capexHasChanged();
    this.setSaved();
  }

  private investmentHasMaintenance(investment: KPI.Investment): boolean {
    return !!(investment as any).investment_type_object?.maintenance;
  }

  private openNotationDialog(investment: KPI.Investment): Observable<boolean> {
    return new Observable(observer => {
      const investmentAssetId = (investment as any).asset;
      const resolvedAsset$ = (investment as any).asset_object
        ? of((investment as any).asset_object)
        : investmentAssetId
          ? this.assetsService.getAsset(investmentAssetId)
          : this.asset
            ? of(this.asset)
            : null;

      if (!resolvedAsset$) {
        observer.next(true);
        observer.complete();
        return;
      }

      const asset$ = resolvedAsset$;

      asset$.pipe(first()).subscribe(asset => {
        const budgetAllocationState = {
          price: (investment as any).slices?.[0]?.price,
          final_schedule_to: investment.final_schedule_to,
          status: (investment as any).status_object,
          budget_origin: (investment as any).budget_origin,
          priority: (investment as any).priority,
          budget_scopes: null,
          is_capex: (investment as any).is_capex,
          aligned_taxonomy: false,
          price_sheet_id: (investment as any).price_sheet,
        };

        this.investmentNotationsService.loadInvestmentNotationsByInvestments([investment]).pipe(first()).subscribe(notations => {
          const existingNotation = notations[0];
          const site = asset?.perimeters?.find(p => !p.is_monosite)
            || (investment as any).perimeters?.find((p: KPI.Perimeter) => !p.is_monosite);
          const state: any = {
            'INVESTMENT_TYPE_SELECTION': (investment as any).investment_type_object,
            'ASSET_SELECTION': asset,
            'SITE_SELECTION': site,
            'BUDGET_ALLOCATION': budgetAllocationState,
          };

          state['INVESTMENT_NOTATION_STEP'] = {
            enabled: true,
            note: existingNotation
              ? this.stateLevelsService.getTechnicalStateOptionNoteFromNotation(
                this.stateLevelsService.getTechnicalStateNotationFromLevelOrNote(existingNotation.note)
              )
              : null,
            duration_deviation: (investment as any).duration_deviation,
            lifetime_after_investment: (investment as any).lifetime_after_investment,
          };

          this.ivWizardService.setState('INVESTMENT_TYPE_SELECTION', (investment as any).investment_type_object);
          this.ivWizardService.setState('BUDGET_ALLOCATION', budgetAllocationState);
          this.ivWizardService.setState('ASSET_SELECTION', asset);
          this.ivWizardService.investmentId = investment.id;
          this.ivWizardService.prefill(state, 'INVESTMENT_NOTATION_STEP');

          this.sideFormLevelService.open(InvestmentWizardRootComponent, {
            autoFocus: false,
            closeOnBackdropClick: true,
            hasScroll: false,
            hasBackdrop: true,
            backdropClass: 'document-sheet-backdrop',
            dialogClass: 'doc-linked-equipment-overlay',
          }).onClose.pipe(
            first(),
            switchMap((investmentNotation: any) => {
              if (!investmentNotation) {
                observer.next(false);
                observer.complete();
                return of(null);
              }

              const hasNoImpact = !investmentNotation.enabled;
              this.ivWizardService.setState('INVESTMENT_TYPE_SELECTION', (investment as any).investment_type_object);
              this.ivWizardService.setState('ASSET_SELECTION', asset);
              this.ivWizardService.setState('INVESTMENT_NOTATION_STEP', investmentNotation);

              return this.investmentsService.updateInvestment(investment.id, { has_no_impact: hasNoImpact }).pipe(
                first(),
                switchMap(() => this.ivWizardService.createInvestmentNotationAndDurationDeviation(investment as any, true))
              );
            })
          ).subscribe({
            next: () => {
              observer.next(true);
              observer.complete();
            },
            error: (err) => observer.error(err)
          });
        });
      });
    });
  }

  public toggleInvestmentSelection(investmentId: number): void {
    if (this.readOnly) {
      return;
    }

    if (this.multipleChoice && !this.asset) {
      const isCurrentlySelected = this.selectedIds().includes(investmentId);

      if (isCurrentlySelected) {
        this.selectedIds.update(ids => ids.filter(id => id !== investmentId));
      } else {
        const investment = this.allElements().find(el => el.id === investmentId);
        if (!investment) return;

        // Complementary investment linking — skip notation dialog
        if (this.mainInvestmentId) {
          this.selectedIds.update(ids => [...ids, investmentId]);
          return;
        }

        if (!(investment as any).investment_type_object) {
          this.investmentsService.getInvestment(investmentId).pipe(first()).subscribe(fullInvestment => {
            if (this.investmentHasMaintenance(fullInvestment)) {
              this.openNotationDialog(fullInvestment).pipe(first()).subscribe({
                next: saved => {
                  if (saved) {
                    this.selectedIds.update(ids => [...ids, investmentId]);
                  }
                },
              });
            } else {
              this.selectedIds.update(ids => [...ids, investmentId]);
            }
          });
          return;
        }

        if (this.investmentHasMaintenance(investment)) {
          this.openNotationDialog(investment).pipe(first()).subscribe({
            next: saved => {
              if (saved) {
                this.selectedIds.update(ids => [...ids, investmentId]);
              }
            },
          });
        } else {
          this.selectedIds.update(ids => [...ids, investmentId]);
        }
      }
    } else {
      this.selectInvestment(investmentId);
    }
  }

  public selectInvestment(investmentId: number): void {
    if ((this.multipleChoice && !this.asset) || this.readOnly) {
      return;
    }

    const selectedInvestment = this.allElements().find(el => el.id === investmentId);
    if (!selectedInvestment) return;

    if (this.initiative || this.mainInvestmentId) {
      this.proceedWithLinking(investmentId);
      return;
    }

    if (!(selectedInvestment as any).investment_type_object) {
      this.investmentsService.getInvestment(investmentId).pipe(first()).subscribe(fullInvestment => {
        if (this.investmentHasMaintenance(fullInvestment)) {
          this.openNotationDialog(fullInvestment).pipe(first()).subscribe({
            next: saved => {
              if (saved) {
                this.proceedWithLinking(investmentId);
              }
            },
          });
        } else {
          this.proceedWithLinking(investmentId);
        }
      });
      return;
    }

    if (this.investmentHasMaintenance(selectedInvestment)) {
      this.openNotationDialog(selectedInvestment).pipe(first()).subscribe({
        next: saved => {
          if (saved) {
            this.proceedWithLinking(investmentId);
          }
        },
      });
    } else {
      this.proceedWithLinking(investmentId);
    }
  }

  private proceedWithLinking(investmentId: number): void {
    if (this.initiative) {
      this.linkInvestmentAndInitiative(investmentId);
    } else if (this.mainInvestmentId) {
      this.selectedIds.set([investmentId]);
      this.linkedElementIds.set([investmentId]);
      this.linkElements();
    } else {
      this.close([investmentId]);
    }
  }

  public showLinkButton(): boolean {
    if (!this.canLink) return false;
    if (this.readOnly) return false;
    if (this.linkedToId) return false;
    // Hide if current investment already has children (can't add a parent to a parent — flat structure)
    if (this.allElements().length > 0) return false;
    if (this.menuMode === 'initiative') {
      return !this.initiative?.investment && this.linkedElementIds().length === 0;
    }
    return true;
  }

  private linkInvestmentAndInitiative(investmentId: number): void {
    this.dialogService.open(
      BasicDialogComponent,
      {
        autoFocus: false,
        context: {
          title: this.translate.instant('Information'),
          content: this.translate.instant('The action will have the same status and date than the attached investment') + '.',
          canCancel: true
        }
      }
    ).onClose.pipe(first()).subscribe(response => {
      if (response) {
        this.selectedIds.set([investmentId]);
        this.linkedElementIds.set([investmentId]);
        this.linkElements();
      }
    });
  }

  openInvestmentForm(investmentId: any): void {
    if (this.currentMode() === 'search') {
      return;
    }
    this.investmentsService.getInvestment(investmentId)
      .pipe(first())
      .subscribe(investment => {
        this.sideFormLevelService.open(
          InvestmentFormComponent,
          {
            autoFocus: false,
            closeOnBackdropClick: false,
            hasScroll: true,
            context: {
              investment: <KPI.MappedInvestment>investment,
            }
          }
        )
      })
  }

  private setSaved(): void {
    this.saveToast.showSaved();
  }
}
